import CloseRounded from '@mui/icons-material/CloseRounded';
import { IconButton, Typography } from '@mui/material';
import { DrawerHeader } from 'components/atomic/molecules/drawer-header';
import Button from 'components/control/button';
import { useCallback, useMemo, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'next-i18next';

import { POI_CATEGORY_MAP, type PoiCategoryId } from 'data/search/poi/poi-categories';
import { LOCALE_COMMON_PATHS, LOCALE_SEARCH_PATHS, Namespaces } from 'types/locales';
import { buildClassName } from 'utils/build-class-name';

import styles from './amenities-filter-panel.module.scss';

const poiCategoryIds = Object.keys(POI_CATEGORY_MAP) as PoiCategoryId[];

export interface AmenitiesFilterContentProps {
  onClose: () => void;
  selectedIds: ReadonlySet<PoiCategoryId>;
  setSelectedIds: Dispatch<SetStateAction<ReadonlySet<PoiCategoryId>>>;
}

/** Amenities/ POIs filter chips panel layout. */
export function AmenitiesFilterContent({
  onClose,
  selectedIds,
  setSelectedIds,
}: AmenitiesFilterContentProps) {
  const { t } = useTranslation([Namespaces.SEARCH, Namespaces.COMMON]);

  const poiCategories = useMemo(
    () => [...poiCategoryIds].sort((a, b) =>
      t(POI_CATEGORY_MAP[a].translationKey).length - t(POI_CATEGORY_MAP[b].translationKey).length
    ),
    [t]
  );

  const select = useCallback(
    (id: PoiCategoryId) => {
      setSelectedIds(prev => new Set(prev).add(id));
    },
    [setSelectedIds]
  );

  const deselect = useCallback(
    (id: PoiCategoryId) => {
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    },
    [setSelectedIds]
  );

  return (
    <>
      <DrawerHeader className={styles.drawerHeader}>
        <div className={styles.drawerHeaderRow}>
          <h3 className={styles.drawerTitle}>{t(LOCALE_SEARCH_PATHS.POI_AMENITIES_FILTER_PANEL_TITLE)}</h3>
        </div>
      </DrawerHeader>
      <div className={styles.body} data-testid="amenities-filter-panel-body">
        <div className={styles.chipRow} role="list" data-testid="poi-category-groups">
          {poiCategories.map(id => {
            const def = POI_CATEGORY_MAP[id];
            const markerSrc = POI_CATEGORY_MAP[id].icon;
            const label = t(def.translationKey);
            const selected = selectedIds.has(id);

            return (
              <div
                key={id}
                className={buildClassName(styles.chipShell, selected && styles.chipShellSelected)}
                data-testid={`poi-category-chip-${id}`}
                role="listitem"
              >
                {!selected ? (
                  <button type="button" className={styles.chipLead} onClick={() => select(id)}>
                    <img src={markerSrc} alt="" className={styles.chipIcon} width={12} height={12} decoding="async" />
                    <Typography variant="body2" component="span">
                      {label}
                    </Typography>
                  </button>
                ) : (
                  <button type="button" className={styles.chipLeadStatic} onClick={() => deselect(id)}>
                    <img src={markerSrc} alt="" className={styles.chipIcon} width={12} height={12} decoding="async" />
                    <Typography variant="body2" component="span">
                      {label}
                    </Typography>
                  </button>
                )}
                <div className={styles.chipTrailing}>
                  {selected ? (
                    <IconButton
                      type="button"
                      size="small"
                      disableRipple
                      className={styles.chipClear}
                      aria-label={t(LOCALE_SEARCH_PATHS.POI_AMENITIES_FILTER_PANEL_REMOVE_CATEGORY, {
                        category: label,
                      })}
                      onClick={event => {
                        event.stopPropagation();
                        deselect(id);
                      }}
                    >
                      <CloseRounded sx={{ fontSize: 12, width: 12, height: 12 }} />
                    </IconButton>
                  ) : (
                    <span className={styles.chipTrailingReserve} aria-hidden />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className={styles.footer}>
          <Button
            type="button"
            theme="primary"
            className={styles.footerButton}
            label={t(LOCALE_COMMON_PATHS.BUTTON_DONE, { defaultValue: 'Done' })}
            onClick={onClose}
            testId="amenities-filter-panel-done"
          />
        </div>
      </div>
    </>
  );
}
